import React, { useState } from 'react';
import dayjs from 'dayjs';
import { MenuItem, TextField } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DocumentFormPopup, FormSection, Field, FieldPair, DateField, formStyles as styles } from './DocumentFormPopup';
import InsuranceDocumentFilesField from '../../../../../components/insurance-documents/InsuranceDocumentFilesField';
import { INSURANCE_AUTHORIZATION_CONTEXT } from '../../../../../components/insurance-documents/InsuranceDocumentAttachments';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { releaseUploadItem, uploadAttachmentItems } from '../../../../../popup/PatientAttachmentUploadPopup/uploadAttachments';
import {
  cardsForProvider, defaultProviderId, formatDate, fromApiDate, isIncompleteDate, isValidDate, toApiDate,
} from './documentFormat';
import {
  AUTHORIZATION_STATUS_OPTIONS, AUTHORIZATION_TYPE_OPTIONS, insuranceCardLabel,
} from '../../../../appointments/v2/utils/billingType';

const NOTES_MAX = 500;
const NONE = '';

// A operadora já está escolhida no campo acima: a opção mostra só plano e número
const cardOptionLabel = (card) => [card.plan_name, card.card_number].filter(Boolean).join(' · ') || insuranceCardLabel(card);

const isActive = (item) => item.active !== 0 && item.active !== false;

const hasValue = (value) => value !== undefined && value !== null && value !== '';

/**
 * Criar ou editar uma guia/autorização. Operadora obrigatória; carteirinha e
 * pedido ligado são opcionais. A situação é sempre manual: o formulário diz
 * isso em texto para ninguém achar que o número confirma a autorização.
 * `defaultCardId` (só na criação) já escolhe a carteirinha e a operadora dela,
 * como no atendimento por convênio.
 *
 * `onSave(values)` devolve uma Promise com { ok, error, id } (quem grava é o dono).
 * Com `patientId`, o formulário ganha o campo "Anexar guia" (foto ou PDF), no
 * mesmo padrão do pedido médico: os arquivos sobem depois de gravar, com o id
 * devolvido por `onSave` (no "Editar", o id da própria guia). Se algum arquivo
 * falhar, a guia já está salva: o popup fica aberto só com o que falhou e
 * "Salvar" tenta de novo, sem gravar a guia outra vez.
 *
 * `onSaved(saved)` (opcional) roda depois de tudo, antes de fechar, com a guia
 * gravada: { id, ...valores, attachments_uploaded }.
 */
function AuthorizationFormPopup({
  authorization, insurances, providers, requests, defaultCardId, patientId, onSave, onSaved, onClose,
}) {
  const isEdit = !!authorization;
  const { UploadPatientAttachment } = DataProviderInstance();
  const canAttach = hasValue(patientId);

  const defaultCard = !isEdit && defaultCardId != null
    ? (insurances || []).find((card) => isActive(card) && String(card.id) === String(defaultCardId))
    : null;
  let initialProvider = defaultProviderId(insurances, providers);
  if (isEdit) initialProvider = String(authorization.provider_id || '');
  else if (defaultCard) initialProvider = String(defaultCard.provider_id);
  const initialCards = cardsForProvider(insurances, initialProvider);
  let initialCard = initialCards.length === 1 ? String(initialCards[0].id) : NONE;
  if (isEdit) initialCard = String(authorization.patient_insurance_id || '');
  else if (defaultCard) initialCard = String(defaultCard.id);

  // Nova guia com um único pedido ativo: ele já vem escolhido (dá para trocar para "Nenhum")
  const activeRequests = (requests || []).filter(isActive);
  let initialRequest = String(authorization?.medical_request_id || '');
  if (!isEdit && activeRequests.length === 1) initialRequest = String(activeRequests[0].id);

  const [form, setForm] = useState({
    provider_id: initialProvider,
    patient_insurance_id: initialCard,
    medical_request_id: initialRequest,
    authorization_type: authorization?.authorization_type || 'consultation',
    reference_number: authorization?.reference_number || '',
    password: authorization?.password || '',
    status: authorization?.status || 'pending',
    valid_from: fromApiDate(authorization?.valid_from),
    valid_until: fromApiDate(authorization?.valid_until),
    authorized_quantity: authorization?.authorized_quantity ? String(authorization.authorized_quantity) : '',
    notes: authorization?.notes || '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState('');
  // Guia já gravada cujos anexos falharam: o próximo "Salvar" só reenvia os arquivos
  const [saved, setSaved] = useState(null);

  const cards = cardsForProvider(insurances, form.provider_id);
  // Em edição, o pedido ligado pode estar inativo: continua na lista para não sumir da tela
  const linkedInactive = isEdit && authorization.medical_request_id
    && !activeRequests.some((item) => String(item.id) === String(authorization.medical_request_id))
    ? (requests || []).find((item) => String(item.id) === String(authorization.medical_request_id))
    : null;
  const requestOptions = linkedInactive ? [...activeRequests, linkedInactive] : activeRequests;

  const change = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] || prev.general ? { ...prev, [name]: undefined, general: undefined } : prev));
  };

  const changeProvider = (providerId) => {
    const nextCards = cardsForProvider(insurances, providerId);
    const keep = nextCards.some((card) => String(card.id) === form.patient_insurance_id);
    setForm((prev) => ({
      ...prev,
      provider_id: providerId,
      patient_insurance_id: keep ? prev.patient_insurance_id : (nextCards.length === 1 ? String(nextCards[0].id) : NONE),
    }));
    setErrors((prev) => ({ ...prev, provider_id: undefined, general: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.provider_id) next.provider_id = 'Escolha a operadora';
    if (isIncompleteDate(form.valid_from)) next.valid_from = 'Data incompleta';
    if (isIncompleteDate(form.valid_until)) {
      next.valid_until = 'Data incompleta';
    } else if (isValidDate(form.valid_from) && isValidDate(form.valid_until) && dayjs(form.valid_until).isBefore(dayjs(form.valid_from), 'day')) {
      next.valid_until = 'O vencimento não pode ser antes do início';
    }
    if (form.authorized_quantity !== '' && (!/^\d+$/.test(form.authorized_quantity) || Number(form.authorized_quantity) < 1)) {
      next.authorized_quantity = 'Informe um número inteiro maior que zero';
    }
    if (form.notes.length > NOTES_MAX) next.notes = `Até ${NOTES_MAX} caracteres`;
    return next;
  };

  const submit = async () => {
    const next = validate();
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setSaving(true);
    let current = saved;
    if (!current) {
      const values = {
        provider_id: form.provider_id,
        patient_insurance_id: form.patient_insurance_id || '',
        medical_request_id: form.medical_request_id || '',
        authorization_type: form.authorization_type,
        reference_number: form.reference_number.trim(),
        password: form.password.trim(),
        status: form.status,
        valid_from: toApiDate(form.valid_from),
        valid_until: toApiDate(form.valid_until),
        authorized_quantity: form.authorized_quantity === '' ? '' : Number(form.authorized_quantity),
        notes: form.notes.trim(),
      };
      const result = await onSave(values);
      if (!result?.ok) {
        setSaving(false);
        setErrors({ general: result?.error || 'Não foi possível salvar a guia. Tente de novo.' });
        return;
      }
      current = { ...values, id: hasValue(result.id) ? result.id : authorization?.id ?? null, attachments_uploaded: 0 };
    }

    const done = await uploadFiles(current);
    setSaving(false);
    if (!done) return;

    if (onSaved) onSaved(done);
    onClose();
  };

  /**
   * Envia os arquivos escolhidos com o id da guia já gravada.
   * @returns {Promise<object|null>} a guia gravada, ou null se sobrou arquivo por enviar
   */
  const uploadFiles = async (current) => {
    if (!canAttach || files.length === 0) return current;

    if (!hasValue(current.id)) {
      setSaved(current);
      setErrors({ general: 'A guia foi salva, mas o anexo não foi enviado. Anexe pela ficha da pessoa.' });
      return null;
    }

    // A data do anexo é o início da guia, quando há e não é futura (o envio não aceita data futura)
    const from = current.valid_from ? dayjs(current.valid_from) : null;
    const documentDate = from && from.isValid() && !from.isAfter(dayjs(), 'day') ? current.valid_from : dayjs().format('YYYY-MM-DD');
    const title = current.reference_number ? `Guia ${current.reference_number}` : 'Guia';

    const result = await uploadAttachmentItems(UploadPatientAttachment, files, {
      patientId,
      context: INSURANCE_AUTHORIZATION_CONTEXT,
      contextId: current.id,
      documentDate,
    }, {
      buildItemPayload: () => ({ title }),
      onProgress: (position, total) => setProgress(`Enviando anexo ${position} de ${total}…`),
    });
    setProgress('');

    const next = { ...current, attachments_uploaded: (current.attachments_uploaded || 0) + result.success };
    if (result.failed > 0) {
      setFiles(result.failedItems);
      setSaved(next);
      setErrors({ general: `A guia foi salva, mas ${result.failed === 1 ? 'um arquivo não foi enviado' : `${result.failed} arquivos não foram enviados`}. ${result.lastError || ''} Salve de novo para tentar enviar.`.trim() });
      return null;
    }

    files.forEach(releaseUploadItem);
    setFiles([]);
    return next;
  };

  // Fechar sem salvar: a guia gravada (anexo pendente) ainda avisa o dono
  const close = () => {
    files.forEach(releaseUploadItem);
    if (saved && onSaved) onSaved(saved);
    onClose();
  };

  const locked = saving || !!saved;

  // displayEmpty: a opção vazia ("Nenhum", "Não informar") aparece escrita no campo
  const selectProps = {
    fullWidth: true, size: 'small', select: true, disabled: locked, className: styles.input,
    SelectProps: { displayEmpty: true },
  };

  return (
    <DocumentFormPopup
      title={isEdit ? 'Editar guia' : 'Nova guia'}
      submitLabel={saved ? 'Enviar anexo de novo' : (isEdit ? 'Salvar' : 'Registrar guia')}
      saving={saving}
      generalError={errors.general}
      onSubmit={submit}
      onClose={close}
    >
      <FormSection title="Cobertura">
        <Field label="Operadora *" htmlFor="authorization-provider">
          <TextField
            {...selectProps}
            id="authorization-provider"
            value={form.provider_id}
            onChange={(e) => changeProvider(e.target.value)}
            error={!!errors.provider_id}
            helperText={errors.provider_id || ((providers || []).length === 0 ? 'Cadastre uma operadora em Cadastros > Convênios' : undefined)}
          >
            {(providers || []).map((provider) => (
              <MenuItem key={provider.Id} value={String(provider.Id)}>{provider.Label}</MenuItem>
            ))}
          </TextField>
        </Field>
        <FieldPair>
          <Field label="Carteirinha" htmlFor="authorization-card">
            <TextField
              {...selectProps}
              id="authorization-card"
              value={form.patient_insurance_id}
              onChange={(e) => change('patient_insurance_id', e.target.value)}
              disabled={locked || !form.provider_id}
              helperText={form.provider_id && cards.length === 0 ? 'Nenhuma carteirinha ativa desta operadora' : undefined}
            >
              <MenuItem value={NONE}><em>Não informar</em></MenuItem>
              {cards.map((card) => (
                <MenuItem key={card.id} value={String(card.id)}>{cardOptionLabel(card)}</MenuItem>
              ))}
            </TextField>
          </Field>
          <Field label="Pedido médico ligado" htmlFor="authorization-request">
            <TextField
              {...selectProps}
              id="authorization-request"
              value={form.medical_request_id}
              onChange={(e) => change('medical_request_id', e.target.value)}
              helperText={requestOptions.length === 0 ? 'Nenhum pedido ativo registrado' : undefined}
            >
              <MenuItem value={NONE}><em>Nenhum</em></MenuItem>
              {requestOptions.map((item) => (
                <MenuItem key={item.id} value={String(item.id)}>
                  {item.title}{item.issued_at ? ` · ${formatDate(item.issued_at)}` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Field>
        </FieldPair>
      </FormSection>

      <FormSection title="Guia">
        <FieldPair>
          <Field label="Tipo" htmlFor="authorization-type">
            <TextField
              {...selectProps}
              id="authorization-type"
              value={form.authorization_type}
              onChange={(e) => change('authorization_type', e.target.value)}
            >
              {AUTHORIZATION_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Field>
          <Field label="Número da guia" htmlFor="authorization-number">
            <TextField
              id="authorization-number"
              value={form.reference_number}
              onChange={(e) => change('reference_number', e.target.value)}
              disabled={locked}
              fullWidth
              size="small"
              placeholder="Como aparece no portal da operadora"
              className={styles.input}
            />
          </Field>
        </FieldPair>
        <FieldPair>
          <Field label="Senha de autorização" htmlFor="authorization-password">
            <TextField
              id="authorization-password"
              value={form.password}
              onChange={(e) => change('password', e.target.value)}
              disabled={locked}
              fullWidth
              size="small"
              autoComplete="off"
              helperText="Não aparece na lista, só ao abrir a guia"
              className={styles.input}
            />
          </Field>
          <Field label="Quantidade autorizada" htmlFor="authorization-quantity">
            <TextField
              id="authorization-quantity"
              type="number"
              value={form.authorized_quantity}
              onChange={(e) => change('authorized_quantity', e.target.value)}
              disabled={locked}
              fullWidth
              size="small"
              inputProps={{ min: 1, step: 1 }}
              placeholder="Sessões ou procedimentos"
              error={!!errors.authorized_quantity}
              helperText={errors.authorized_quantity}
              className={styles.input}
            />
          </Field>
        </FieldPair>
        <FieldPair>
          <Field label="Vale a partir de" htmlFor="authorization-valid-from">
            <DateField
              id="authorization-valid-from"
              value={form.valid_from}
              onChange={(value) => change('valid_from', value)}
              disabled={locked}
              error={errors.valid_from}
            />
          </Field>
          <Field label="Vencimento em" htmlFor="authorization-valid-until">
            <DateField
              id="authorization-valid-until"
              value={form.valid_until}
              onChange={(value) => change('valid_until', value)}
              disabled={locked}
              minDate={form.valid_from || undefined}
              error={errors.valid_until}
              helperText="Em branco: sem vencimento"
            />
          </Field>
        </FieldPair>
      </FormSection>

      <FormSection title="Situação">
        <Field htmlFor="authorization-status">
          <TextField
            {...selectProps}
            id="authorization-status"
            value={form.status}
            onChange={(e) => change('status', e.target.value)}
          >
            {AUTHORIZATION_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
            ))}
          </TextField>
        </Field>
        <div className={styles.notice}>
          <InfoOutlinedIcon className={styles.noticeIcon} />
          <span>A situação é registrada manualmente. Cadastrar o número não quer dizer que a operadora autorizou.</span>
        </div>
      </FormSection>

      {canAttach && (
        <FormSection title="Anexar guia">
          <InsuranceDocumentFilesField
            patientId={patientId}
            context={INSURANCE_AUTHORIZATION_CONTEXT}
            contextId={isEdit ? authorization.id : saved?.id}
            items={files}
            onItemsChange={setFiles}
            disabled={saving}
            onError={(message) => setErrors((prev) => ({ ...prev, general: message }))}
          />
          {progress && <span className={styles.hint}>{progress}</span>}
        </FormSection>
      )}

      <FormSection title="Observações">
        <Field htmlFor="authorization-notes">
          <TextField
            id="authorization-notes"
            value={form.notes}
            onChange={(e) => change('notes', e.target.value)}
            disabled={locked}
            fullWidth
            size="small"
            multiline
            rows={3}
            placeholder="Opcional. Retorno da operadora, protocolo, combinados"
            error={!!errors.notes}
            helperText={errors.notes || `${form.notes.length}/${NOTES_MAX}`}
            className={styles.input}
          />
        </Field>
      </FormSection>
    </DocumentFormPopup>
  );
}

export default AuthorizationFormPopup;
